import { useState, useRef } from 'react';
import { Camera, Upload, X, SpinnerGap, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export const BillScanner = ({ onExtracted, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    // Preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }

    // Upload and scan
    setScanning(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/tasks/scan-bill`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (res.data.success) {
        onExtracted(res.data.extracted);
      } else {
        setError(res.data.error || 'Could not extract bill data.');
      }
    } catch (err) {
      setError('Failed to scan. Try a clearer image.');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="bill-scanner">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#f2f4f6]">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-[#005bc1]" weight="duotone" />
            <span className="font-bold text-sm text-[#2d3338]">Scan Bill</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#f2f4f6]" data-testid="close-scanner">
            <X size={18} className="text-[#596065]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {scanning ? (
            <div className="flex flex-col items-center py-8 gap-3">
              {preview && <img src={preview} alt="Bill" className="w-32 h-32 object-cover rounded-xl border border-[#e5e7eb]" />}
              <SpinnerGap size={28} className="text-[#005bc1] animate-spin" />
              <p className="text-sm font-medium text-[#2d3338]">Scanning bill...</p>
              <p className="text-xs text-[#596065]">Extracting provider, amount, due date</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-6 gap-3">
              <p className="text-sm text-[#dc2626] text-center">{error}</p>
              <button onClick={() => { setError(''); setPreview(null); }} className="text-xs text-[#005bc1] font-semibold hover:underline">Try again</button>
            </div>
          ) : (
            <>
              <p className="text-xs text-[#596065] text-center">Upload or take a photo of your bill. AI will extract the details automatically.</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraRef.current?.click()}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-[#d0e4f7] bg-[#f0f7ff] hover:bg-[#e0efff] hover:border-[#005bc1] transition-all"
                  data-testid="scan-camera-btn"
                >
                  <Camera size={28} className="text-[#005bc1]" weight="duotone" />
                  <span className="text-xs font-semibold text-[#005bc1]">Take Photo</span>
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-[#e0d4f5] bg-[#faf8ff] hover:bg-[#f0ecff] hover:border-[#7c3aed] transition-all"
                  data-testid="scan-upload-btn"
                >
                  <Upload size={28} className="text-[#7c3aed]" weight="duotone" />
                  <span className="text-xs font-semibold text-[#7c3aed]">Upload File</span>
                </button>
              </div>
              <p className="text-[10px] text-[#acb3b8] text-center">Supports photos, screenshots, and PDF bills</p>
            </>
          )}
        </div>

        {/* Hidden file inputs */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
      </div>
    </div>
  );
};
